// screens-main.jsx — tab screens (return fragments; App provides shell + TabBar)
import React from 'react';
import {
  kr, num, fmtMin, fmtHM, STATUS, STATUS_ORDER, ARTICLES, artByCode,
  TEMPLATES, JOBS, VAT, rowAmount, reportTotal, rowLabel, rowUnit, rowRate,
  TODAY, isoLocal, fmtDateLong, fmtDateShort, APP, COMPANY, uid,
  WORK_START_MIN, WORK_END_MIN, hmToMin, minToHM, ceilMin, lunchWindow, lunchAbsorb, groupParts, nextGroupId,
  travelBetween, prevJobSameDay, resolveDeparture,
  Btn, StatusBadge, SecLabel, Card, Avatar, AppHeader, TabBar, TabItem,
  Stepper, Field, Sheet, MapMock, CalendarSheet, Toggle, ProfileButton,
  Icon, ICON_PATHS,
} from '../shared.js';
const { useState: useStateM, useMemo, useEffect: useEffectM } = React;

// ── Shared job card ─────────────────────────────────────────
function Meta({ icon, children, strong }) {
  return (
    <span className="gv-meta" style={{ color: strong ? 'var(--ink)' : 'var(--ink-2)' }}>
      <Icon name={icon} size={15} stroke={1.9} color="var(--ink-3)" />
      <span style={{ fontWeight: strong ? 650 : 500 }}>{children}</span>
    </span>
  );
}

function JobCard({ job, onOpen, showDate, onMove }) {
  const addr = job.address.split(',')[0];
  return (
    <Card onClick={() => onOpen(job)} className="gv-fade" style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="gv-tnum" style={{ fontSize: 14.5, fontWeight: 700 }}>
            {showDate ? fmtDateShort(job.date) : job.startTime}
          </span>
          <span style={{ color: 'var(--line-2)' }}>·</span>
          <span className="gv-mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>{job.id}</span>
        </div>
        <StatusBadge status={job.status} size="sm" />
      </div>
      <div style={{ fontSize: 17, fontWeight: 700, marginTop: 9, letterSpacing: '-.01em', lineHeight: 1.2 }}>{job.title}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
        <span style={{ fontSize: 13.5, color: 'var(--ink-2)' }}>{job.customer}</span>
        {job.partTotal > 1 && <span className="gv-badge is-sm" style={{ background: 'var(--accent-wash)', color: 'var(--accent-ink)' }}><Icon name="layers" size={11} stroke={2} /> Del {job.partIndex}/{job.partTotal}</span>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 12, paddingTop: 11, borderTop: '1px solid var(--line)', flexWrap: 'wrap' }}>
        <Meta icon="pin">{addr}</Meta>
        <Meta icon="clock">{fmtMin(job.estMinutes)}</Meta>
        {onMove && (
          <button onClick={(e) => { e.stopPropagation(); onMove(job); }} className="gv-chip" style={{ marginLeft: 'auto', height: 30 }}>
            <Icon name="clock" size={14} stroke={1.9} /> Omplanera
          </button>
        )}
      </div>
    </Card>
  );
}

// ── Empty state ─────────────────────────────────────────────
function Empty({ icon, title, sub }) {
  return (
    <div style={{ textAlign: 'center', padding: '46px 24px', color: 'var(--ink-3)' }}>
      <div style={{ width: 56, height: 56, borderRadius: 16, background: 'var(--surface)', border: '1px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
        <Icon name={icon} size={26} stroke={1.8} color="var(--ink-3)" />
      </div>
      <div style={{ fontWeight: 700, color: 'var(--ink-2)', fontSize: 15 }}>{title}</div>
      {sub && <div style={{ fontSize: 13.5, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function Stat({ label, value, suffix, small }) {
  return (
    <div style={{ flex: 1, padding: '15px 14px' }}>
      <div className="gv-tnum" style={{ fontSize: small ? 19 : 24, fontWeight: 780, letterSpacing: '-.02em', lineHeight: 1 }}>
        {value}{suffix && <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-3)', marginLeft: 3 }}>{suffix}</span>}
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 6, fontWeight: 600 }}>{label}</div>
    </div>
  );
}

// ── TODAY ───────────────────────────────────────────────────
function addDaysISO(iso, n) {
  const d = new Date(iso + 'T00:00:00'); d.setDate(d.getDate() + n);
  return isoLocal(d);
}
function relDayLabel(iso) {
  if (iso === TODAY) return 'Idag';
  const t = new Date(TODAY + 'T00:00:00'), d = new Date(iso + 'T00:00:00');
  const diff = Math.round((d - t) / 86400000);
  if (diff === 1) return 'Imorgon';
  if (diff === -1) return 'Igår';
  const w = d.toLocaleDateString('sv-SE', { weekday: 'long' });
  return w.charAt(0).toUpperCase() + w.slice(1);
}

function TodayScreen({ jobs, onOpen, onOpenProfile, onMove }) {
  const [sel, setSel] = useStateM(TODAY);
  const isToday = sel === TODAY;
  const dayJobs = jobs.filter(j => j.date === sel);
  const planned = dayJobs.filter(j => j.status === 'planerad').sort((a, b) => a.startTime.localeCompare(b.startTime));
  const done = dayJobs.filter(j => j.status === 'rapporterad' || j.status === 'fakturerad').sort((a, b) => a.startTime.localeCompare(b.startTime));
  const totalMin = planned.reduce((s, j) => s + j.estMinutes, 0);
  const remaining = planned.length;

  return (
    <>
      <AppHeader large subtitle={fmtDateLong(sel)} title={relDayLabel(sel)}
        right={<ProfileButton onClick={onOpenProfile} />} />

      {/* day navigator */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '0 18px 12px', flexShrink: 0 }}>
        <button onClick={() => setSel(TODAY)} disabled={isToday}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 38, padding: '0 15px', borderRadius: 11, border: '1px solid ' + (isToday ? 'var(--line-2)' : 'var(--accent)'), background: isToday ? 'var(--surface)' : 'var(--accent-wash)', color: isToday ? 'var(--ink-3)' : 'var(--accent-ink)', fontWeight: 700, fontSize: 14, opacity: isToday ? 0.55 : 1 }}>
          {!isToday && <Icon name="back" size={15} stroke={2.3} />} Idag
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="gv-iconbtn" onClick={() => setSel(addDaysISO(sel, -1))} aria-label="Föregående dag"><Icon name="chevL" size={20} stroke={2.2} /></button>
          <button className="gv-iconbtn" onClick={() => setSel(addDaysISO(sel, 1))} aria-label="Nästa dag"><Icon name="chevR" size={20} stroke={2.2} /></button>
        </div>
      </div>

      <div className="gv-scroll" style={{ paddingTop: 2 }}>
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ display: 'flex' }}>
            <Stat label="Planerade" value={remaining} />
            <div style={{ width: 1, background: 'var(--line)' }} />
            <Stat label="Planerad tid" value={fmtHM(totalMin)} suffix="tim" />
            <div style={{ width: 1, background: 'var(--line)' }} />
            <Stat label="Utgår från" value="Ringön" small />
          </div>
        </Card>

        <SecLabel action={planned.length ? `${planned.length} st` : null}>{isToday ? 'Dagens planerade uppdrag' : 'Planerade uppdrag'}</SecLabel>
        {planned.length ? planned.map(j => <JobCard key={j.id} job={j} onOpen={onOpen} onMove={onMove} />)
          : <Empty icon={done.length ? 'checkCircle' : 'calendar'} title={done.length ? 'Allt rapporterat' : 'Inga planerade uppdrag'} sub={done.length ? 'Alla dagens uppdrag är klara.' : `Inget inbokat ${relDayLabel(sel).toLowerCase()}.`} />}

        {done.length > 0 && (
          <>
            <SecLabel action={`${done.length} st`}>Rapporterat</SecLabel>
            <div className="gv-list">
              {done.map(j => <DoneRow key={j.id} job={j} onOpen={onOpen} />)}
            </div>
          </>
        )}
      </div>
    </>
  );
}

function DoneRow({ job, onOpen }) {
  const total = reportTotal(job.report);
  const invoiced = job.status === 'fakturerad';
  return (
    <div className="gv-lrow is-tap" onClick={() => onOpen(job)} style={{ padding: '11px 14px' }}>
      <div className="gv-lrow-ic" style={{ width: 30, height: 30, borderRadius: 9, background: 'var(--ok-wash)', color: 'var(--ok-ink)' }}>
        <Icon name="check" size={16} stroke={2.7} />
      </div>
      <div className="gv-lrow-tt" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <b style={{ fontSize: 14, fontWeight: 650, color: 'var(--ink-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>{job.title}</b>
          <span className="gv-tnum" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{job.startTime} · {invoiced ? job.invoiceNo : 'Rapporterad'}</span>
        </div>
        <span className="gv-tnum" style={{ fontSize: 13.5, fontWeight: 760, color: 'var(--ok-ink)', whiteSpace: 'nowrap', flexShrink: 0 }}>{kr(total)}</span>
      </div>
    </div>
  );
}

// ── JOBS LIST ───────────────────────────────────────────────
function JobsScreen({ jobs, onOpen }) {
  const [filter, setFilter] = useStateM('alla');
  const filters = [['alla', 'Alla'], ['planerad', 'Planerad'], ['rapporterad', 'Rapporterad'], ['fakturerad', 'Fakturerad']];
  const list = useMemo(() => {
    const arr = filter === 'alla' ? jobs.slice() : jobs.filter(j => j.status === filter);
    return arr.sort((a, b) => (b.date + b.startTime).localeCompare(a.date + a.startTime));
  }, [jobs, filter]);

  return (
    <>
      <AppHeader large title="Uppdrag"
        right={<button className="gv-iconbtn" aria-label="Sök"><Icon name="search" size={21} stroke={1.9} /></button>} />
      <div style={{ display: 'flex', gap: 8, padding: '4px 18px 12px', overflowX: 'auto', flexShrink: 0 }}>
        {filters.map(([k, lbl]) => (
          <button key={k} className={'gv-chip' + (filter === k ? ' is-on' : '')} onClick={() => setFilter(k)} style={{ flexShrink: 0 }}>{lbl}</button>
        ))}
      </div>
      <div className="gv-scroll" style={{ paddingTop: 0 }}>
        {list.length ? list.map(j => <JobCard key={j.id} job={j} onOpen={onOpen} showDate />)
          : <Empty icon="list" title="Inga uppdrag" sub="Inget uppdrag med denna status." />}
      </div>
    </>
  );
}

// ── INVOICE / FOLLOW-UP ─────────────────────────────────────
function InvoiceScreen({ jobs, onOpen, onInvoice, onOpenProfile }) {
  const ready = jobs.filter(j => j.status === 'rapporterad');
  const done = jobs.filter(j => j.status === 'fakturerad').sort((a, b) => (b.invoicedAt || '').localeCompare(a.invoicedAt || ''));
  const toInvoice = ready.reduce((s, j) => s + reportTotal(j.report), 0);

  return (
    <>
      <AppHeader large title="Fakturering" right={<ProfileButton onClick={onOpenProfile} />} />
      <div className="gv-scroll">
        <Card style={{ marginTop: 4, background: 'var(--ink)', borderColor: 'var(--ink)', color: '#fff' }}>
          <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,.6)', fontWeight: 600, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Att fakturera</div>
          <div className="gv-tnum" style={{ fontSize: 33, fontWeight: 800, letterSpacing: '-.02em', marginTop: 6 }}>{kr(toInvoice)}</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,.65)', marginTop: 2 }}>{ready.length} rapporterade uppdrag · exkl. moms</div>
        </Card>

        <SecLabel>Klar att fakturera</SecLabel>
        {ready.length ? ready.map(j => (
          <Card key={j.id} className="gv-fade" style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }} onClick={() => onOpen(j)}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 16.5, fontWeight: 700 }}>{j.title}</div>
                <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 1 }}>{j.customer} · {fmtDateShort(j.date)}</div>
              </div>
              <div className="gv-tnum" style={{ fontSize: 17, fontWeight: 760, whiteSpace: 'nowrap' }}>{kr(reportTotal(j.report))}</div>
            </div>
            <div style={{ display: 'flex', gap: 9, marginTop: 13 }}>
              <Btn variant="secondary" size="sm" icon="doc" onClick={() => onOpen(j)} style={{ flex: 1 }}>Visa rapport</Btn>
              <Btn variant="primary" size="sm" icon="check" onClick={() => onInvoice(j)} style={{ flex: 1 }}>Markera fakturerad</Btn>
            </div>
          </Card>
        )) : <Empty icon="checkCircle" title="Inget att fakturera" sub="Rapporterade uppdrag dyker upp här." />}

        {done.length > 0 && <>
          <SecLabel action={`${done.length} st`}>Fakturerade</SecLabel>
          <div className="gv-list">
            {done.map(j => (
              <div key={j.id} className="gv-lrow is-tap" onClick={() => onOpen(j)}>
                <div className="gv-lrow-ic" style={{ background: 'var(--accent-wash)', color: 'var(--accent-ink)' }}>
                  <Icon name="check" size={18} stroke={2.4} />
                </div>
                <div className="gv-lrow-tt">
                  <b>{j.title}</b>
                  <span>{j.invoiceNo} · {fmtDateShort(j.invoicedAt)}</span>
                </div>
                <div className="gv-tnum" style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-2)' }}>{kr(reportTotal(j.report))}</div>
              </div>
            ))}
          </div>
        </>}
      </div>
    </>
  );
}

// ── PROFILE ─────────────────────────────────────────────────
function ScheduleSheet({ open, onClose, settings, setSettings }) {
  const toMin = (t) => { const m = /^(\d{1,2}):(\d{2})$/.exec((t || '').trim()); return m ? +m[1] * 60 + +m[2] : null; };
  const toHM = (n) => `${String(Math.floor(((n % 1440) + 1440) % 1440 / 60)).padStart(2, '0')}:${String(((n % 1440) + 1440) % 1440 % 60).padStart(2, '0')}`;
  const set = (patch) => setSettings(s => ({ ...s, ...patch }));
  const ls = toMin(settings.lunchStart) ?? 720;
  return (
    <Sheet open={open} onClose={onClose} title="Rast & restid" maxH="88%">
      <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.5, marginBottom: 14 }}>
        Appen håller din lunch ledig och reserverar restid mellan uppdrag när du planerar eller söker lediga tider.
      </div>

      {/* Lunch */}
      <div className="gv-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="gv-lrow" style={{ justifyContent: 'space-between' }}>
          <div className="gv-lrow-ic" style={settings.lunchOn ? { background: 'var(--accent-wash)', color: 'var(--accent-ink)' } : null}><Icon name="sun" size={19} stroke={1.9} /></div>
          <div className="gv-lrow-tt" style={{ flex: 1 }}><b style={{ fontSize: 14.5, fontWeight: 650, display: 'block' }}>Planerad lunch</b><span style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>Blockeras i kalendern</span></div>
          <Toggle on={settings.lunchOn} onChange={(v) => set({ lunchOn: v })} />
        </div>
        {settings.lunchOn && <>
          <div className="gv-lrow" style={{ justifyContent: 'space-between' }}>
            <div className="gv-lrow-tt" style={{ flex: 1 }}><b style={{ fontSize: 14, fontWeight: 600 }}>Starttid</b></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button className="gv-iconbtn is-soft" onClick={() => set({ lunchStart: toHM(ls - 15) })} aria-label="Tidigare"><Icon name="minus" size={16} stroke={2.4} /></button>
              <span className="gv-tnum" style={{ fontSize: 15.5, fontWeight: 700, minWidth: 48, textAlign: 'center' }}>{settings.lunchStart}</span>
              <button className="gv-iconbtn is-soft" onClick={() => set({ lunchStart: toHM(ls + 15) })} aria-label="Senare"><Icon name="plus" size={16} stroke={2.4} /></button>
            </div>
          </div>
          <div className="gv-lrow" style={{ justifyContent: 'space-between', borderBottom: 'none' }}>
            <div className="gv-lrow-tt" style={{ flex: 1 }}><b style={{ fontSize: 14, fontWeight: 600 }}>Längd</b></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Stepper value={settings.lunchMinutes} onChange={(v) => set({ lunchMinutes: Math.max(15, v) })} step={15} min={15} size="sm" />
              <span className="gv-affix">min</span>
            </div>
          </div>
        </>}
      </div>

      {/* Travel */}
      <div className="gv-card" style={{ padding: 0, overflow: 'hidden', marginTop: 12 }}>
        <div className="gv-lrow" style={{ justifyContent: 'space-between' }}>
          <div className="gv-lrow-ic" style={(settings.travelOn !== false) ? { background: 'var(--accent-wash)', color: 'var(--accent-ink)' } : null}><Icon name="car" size={19} stroke={1.9} /></div>
          <div className="gv-lrow-tt" style={{ flex: 1 }}><b style={{ fontSize: 14.5, fontWeight: 650, display: 'block' }}>Restid mellan uppdrag</b><span style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>Beräknas via Google Maps</span></div>
          <Toggle on={settings.travelOn !== false} onChange={(v) => set({ travelOn: v })} />
        </div>
        {settings.travelOn !== false &&
          <div className="gv-lrow" style={{ justifyContent: 'space-between', borderBottom: 'none' }}>
            <div className="gv-lrow-tt" style={{ flex: 1 }}><b style={{ fontSize: 14, fontWeight: 600, display: 'block' }}>Marginal i lediga tider</b><span style={{ fontSize: 12, color: 'var(--ink-3)' }}>Används när destination ännu inte valts</span></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Stepper value={settings.travelDefault} onChange={(v) => set({ travelDefault: Math.max(0, v) })} step={5} min={0} size="sm" />
              <span className="gv-affix">min</span>
            </div>
          </div>}
      </div>

      <Btn variant="primary" icon="check" full onClick={onClose} style={{ marginTop: 16 }}>Klar</Btn>
    </Sheet>
  );
}

function ProfileScreen({ jobs, templates, defaultId, onOpenTemplates, onOpenArticles, onBack, settings = {}, setSettings, showToast, onLogout }) {
  const [schedOpen, setSchedOpen] = useStateM(false);
  const [addrOpen, setAddrOpen] = useStateM(false);
  const [genOpen, setGenOpen] = useStateM(false);
  const week = jobs.filter(j => j.report);
  const hours = week.reduce((s, j) => s + (j.report.rows || []).filter(r => r.code === 'ARB').reduce((x, r) => x + (r.qty || 0), 0), 0);
  const revenue = jobs.filter(j => j.status === 'fakturerad').reduce((s, j) => s + reportTotal(j.report), 0);
  const defTpl = templates.find(t => t.id === defaultId);

  const vatPct = Math.round((settings.vat != null ? settings.vat : VAT) * 100);
  const settingsRows = [
    { ic: 'box', t: 'Artikelregister', s: `${ARTICLES.length} artiklar`, onClick: onOpenArticles, hl: true },
    { ic: 'layers', t: 'Rapportmallar', s: defTpl ? `Standard: ${defTpl.name}` : `${templates.length} mallar`, onClick: onOpenTemplates, hl: true },
    { ic: 'sun', t: 'Rast & restid', s: (settings.lunchOn ? `Lunch ${settings.lunchStart} · ${settings.lunchMinutes} min` : 'Lunch av') + ' · ' + (settings.travelOn !== false ? `restid ${settings.travelDefault} min` : 'restid av'), onClick: () => setSchedOpen(true), hl: true },
    { ic: 'pin', t: 'Utgångsadress', s: settings.baseAddress || COMPANY.base, onClick: () => setAddrOpen(true), hl: true },
    { ic: 'bell', t: 'Notiser', s: 'På' },
    { ic: 'cog', t: 'Inställningar', s: `${COMPANY.name} · moms ${vatPct}%`, onClick: () => setGenOpen(true), hl: true },
  ];

  return (
    <>
      <AppHeader large title="Profil" onBack={onBack} />
      <div className="gv-scroll">
        <Card style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 14 }}>
          <Avatar name={COMPANY.user} size={56} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 760 }}>{COMPANY.user}</div>
            <div style={{ fontSize: 13.5, color: 'var(--ink-2)' }}>{COMPANY.role}</div>
            <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 2 }}>{COMPANY.name}</div>
          </div>
        </Card>

        <Card style={{ padding: 0, marginTop: 12, overflow: 'hidden' }}>
          <div style={{ display: 'flex' }}>
            <Stat label="Uppdrag (vecka)" value={week.length} />
            <div style={{ width: 1, background: 'var(--line)' }} />
            <Stat label="Arb.timmar" value={num(hours, 1)} suffix="tim" />
            <div style={{ width: 1, background: 'var(--line)' }} />
            <Stat label="Fakturerat" value={kr(revenue).replace(' kr', '')} suffix="kr" small />
          </div>
        </Card>

        <SecLabel>Inställningar</SecLabel>
        <div className="gv-list">
          {settingsRows.map((r, i) => (
            <div key={i} className="gv-lrow is-tap" onClick={r.onClick}>
              <div className="gv-lrow-ic" style={r.hl ? { background: 'var(--accent-wash)', color: 'var(--accent-ink)' } : null}><Icon name={r.ic} size={19} stroke={1.9} /></div>
              <div className="gv-lrow-tt"><b>{r.t}</b><span>{r.s}</span></div>
              <Icon name="chevR" size={17} stroke={2} color="var(--ink-3)" />
            </div>
          ))}
        </div>

        {onLogout && (
          <div style={{ marginTop: 18 }}>
            <Btn variant="danger" icon="back" full onClick={onLogout}>Logga ut</Btn>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 11, padding: '32px 0 8px' }}>
          <div style={{ width: 62, height: 50, borderRadius: 14, background: '#fff', border: '1px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 5px 14px rgba(0,0,0,.06)' }}>
            <img src="/logoonly.png" alt="" style={{ width: 46, height: 36, objectFit: 'contain', display: 'block' }} />
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 800, letterSpacing: '-.02em', color: 'var(--ink)', fontSize: 16.5 }}>{APP.name}</div>
            <div style={{ fontSize: 11.5, fontFamily: 'var(--mono)', marginTop: 4, color: 'var(--ink-3)' }}>{APP.tagline}</div>
          </div>
        </div>
      </div>
      <ScheduleSheet open={schedOpen} onClose={() => { setSchedOpen(false); showToast && showToast('Rast & restid uppdaterad'); }} settings={settings} setSettings={setSettings} />
      <BaseAddressSheet open={addrOpen} onClose={() => setAddrOpen(false)} settings={settings} setSettings={setSettings} showToast={showToast} />
      <GeneralSettingsSheet open={genOpen} onClose={() => setGenOpen(false)} settings={settings} setSettings={setSettings} showToast={showToast} />
    </>
  );
}

// ── Utgångsadress (base/departure address) ──────────────────
function BaseAddressSheet({ open, onClose, settings, setSettings, showToast }) {
  const [val, setVal] = useStateM('');
  useEffectM(() => { if (open) setVal(settings.baseAddress || COMPANY.base || ''); }, [open]);
  const save = () => {
    const v = val.trim();
    setSettings((s) => ({ ...s, baseAddress: v }));
    onClose();
    showToast && showToast('Utgångsadress sparad');
  };
  return (
    <Sheet open={open} onClose={onClose} title="Utgångsadress">
      <Card style={{ marginBottom: 14, background: 'var(--surface-2)', fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.5 }}>
        Adressen du normalt utgår ifrån (verkstad, kontor, hem). Används för att beräkna restid och reskostnad till första uppdraget.
      </Card>
      <Field label="Adress" value={val} onChange={setVal} placeholder="t.ex. Verkstaden · Ringön 6, Göteborg" multiline />
      <Btn variant="primary" icon="check" full onClick={save} style={{ marginTop: 16 }}>Spara</Btn>
    </Sheet>
  );
}

// ── Inställningar (company details + VAT + locale) ──────────
function GeneralSettingsSheet({ open, onClose, settings, setSettings, showToast }) {
  const [name, setName] = useStateM('');
  const [company, setCompanyName] = useStateM('');
  const [role, setRole] = useStateM('');
  const [vat, setVatText] = useStateM('25');
  useEffectM(() => {
    if (open) {
      setName(COMPANY.user || '');
      setCompanyName(COMPANY.name || '');
      setRole(COMPANY.role || '');
      setVatText(String(Math.round((settings.vat != null ? settings.vat : VAT) * 100)));
    }
  }, [open]);
  const save = () => {
    const vatFrac = Math.max(0, (parseFloat(String(vat).replace(',', '.')) || 0) / 100);
    setSettings((s) => ({
      ...s,
      company: { name: company.trim() || COMPANY.name, user: name.trim() || COMPANY.user, role: role.trim() || COMPANY.role },
      vat: vatFrac,
    }));
    onClose();
    showToast && showToast('Inställningar sparade');
  };
  const infoRow = (t, s) => (
    <div className="gv-lrow"><div className="gv-lrow-tt"><b style={{ fontWeight: 600 }}>{t}</b><span>{s}</span></div></div>
  );
  return (
    <Sheet open={open} onClose={onClose} title="Inställningar" maxH="90%">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field label="Ditt namn" value={name} onChange={setName} placeholder="Marcus Lindberg" />
        <Field label="Företag" value={company} onChange={setCompanyName} placeholder="Lindberg Service & VVS" />
        <Field label="Yrkesroll" value={role} onChange={setRole} placeholder="Servicetekniker" />
        <Field label="Moms" value={vat} onChange={setVatText} mono suffix="%" />
      </div>
      <div style={{ margin: '18px 0 9px', fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--ink-3)' }}>Regionalt</div>
      <div className="gv-list">
        {infoRow('Valuta', 'Svenska kronor (SEK)')}
        {infoRow('Avstånd', 'Kilometer (km)')}
        {infoRow('Språk', 'Svenska')}
      </div>
      <Btn variant="primary" icon="check" full onClick={save} style={{ marginTop: 18 }}>Spara</Btn>
    </Sheet>
  );
}

export { JobCard, Meta, Empty, Stat, TodayScreen, JobsScreen, InvoiceScreen, ProfileScreen, ScheduleSheet };
